import { useEffect, useRef, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import Icon from "../components/Icon"
import { formatMoney } from "../utils/format"
import { useUTBills } from "./utbills/useUTBills"
import BillsView from "./utbills/BillsView"
import BillingConsole from "./utbills/BillingConsole"
import ExpenseRecordModal from "./utbills/ExpenseRecordModal"
import MonthlySummaryPanel from "../components/MonthlySummaryPanel"
import Skeleton, { SkeletonStatGrid, SkeletonTable } from "../components/Skeleton"
import { getUtilityWalletBalance } from "./utbills/utilitySettlement"
import AllTransactionsButton from "../components/allTransactions/AllTransactionsButton"
import AllTransactionsModal from "../components/allTransactions/AllTransactionsModal"
import { BILLS_ALL_TRANSACTIONS } from "../components/allTransactions/allTransactionsConfigs"

const TABS = [
  { key: "bills", label: "Received (Bills)", icon: "file-earmark-text" },
  { key: "billing", label: "Issued (Invoices/Bills)", icon: "box-arrow-up" },
]

const buildUtBillsTransactionsUrl = ({ endpoint, month, year, role }) => {
  if (role === "My Bills") {
    return "/utilities/my-statement"
  }

  return `${endpoint}/transactions?month=${month}&year=${year}&role=${encodeURIComponent(role)}`
}

function UTBillsModule() {
  const navigate = useNavigate()
  const location = useLocation()
  const ut = useUTBills()
  // Honor an explicit landing tab passed via navigation state (e.g. after
  // registering a company from the dashboard), otherwise auto-pick below.
  const [tab, setTab] = useState(location.state?.tab || null)
  const [expenseModalOpen, setExpenseModalOpen] = useState(false)
  const [allTransactionsOpen, setAllTransactionsOpen] = useState(false)
  const [summaryRefreshKey, setSummaryRefreshKey] = useState(0)
  const appliedProviderRef = useRef(false)
  const impersonating = localStorage.getItem("impersonationActive") === "1"

  // Pick the most useful landing tab once data arrives: people who only bill
  // (companies/managers with no personal bills) open straight into the console.
  useEffect(() => {
    if (ut.loading || tab !== null) return
    const billsFirst = !(ut.providers.length > 0 && ut.invoices.length === 0)
    setTab(billsFirst ? "bills" : "billing")
  }, [ut.loading, tab, ut.providers.length, ut.invoices.length])

  // Pre-select a company passed via navigation state, once providers load.
  useEffect(() => {
    if (appliedProviderRef.current || ut.loading) return
    const providerId = location.state?.providerId
    if (!providerId) return
    if (ut.providers.some((provider) => String(provider._id) === String(providerId))) {
      ut.setSelectedProviderId(providerId)
      appliedProviderRef.current = true
    }
  }, [location.state, ut.loading, ut.providers, ut.setSelectedProviderId])

  const activeTab = tab || "bills"
  const renderUtilityExpenseTile = ({ activeSection }) => {
    if (activeSection?.key !== "Billing") return null
    return <UtilityNetCashTile summary={activeSection.summary} />
  }
  const renderExpenseAction = ({ activeSection, stat }) => {
    if (activeSection?.key !== "Billing" || stat?.label !== "Expenses") return null
    return <AddExpenseButton onClick={() => setExpenseModalOpen(true)} />
  }

  // The company already exists by the time this runs, so a failed reload is
  // reported as a refresh problem, not a registration failure.
  const handleProviderRegistered = async () => {
    try {
      await ut.refreshAll()
    } catch {
      ut.setError("Your billing company was created, but the page could not refresh. Please reload.")
    }
  }

  const createExpenseRecord = async (providerId, payload) => {
    await ut.createExpense(providerId, payload)
    setSummaryRefreshKey((current) => current + 1)
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-20 border-b border-teal-800/30 bg-gradient-to-r from-emerald-700 to-teal-700 text-white">
        <div className="mx-auto max-w-6xl px-4 pt-3 pb-2 sm:pt-4">
          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => navigate("/dashboard")}
              className="inline-flex items-center gap-2 rounded-lg bg-white/15 px-3 py-2 text-sm font-semibold transition-colors hover:bg-white/25"
            >
              <Icon name="arrow-left" size="1rem" />
              <span className="hidden sm:inline">Back</span>
            </button>
            <div className="min-w-0 flex-1">
              <h1 className="truncate text-xl font-black sm:text-2xl">Bills &amp; Invoices</h1>
              <p className="hidden truncate text-xs text-emerald-50 sm:block">Pay bills and run your billing companies</p>
            </div>
            <div className="rounded-xl bg-white/15 px-3 py-1.5 text-right">
              <p className="text-[10px] uppercase tracking-wide text-emerald-50">Wallet</p>
              <p className="text-sm font-black">{formatMoney(getUtilityWalletBalance(ut.wallet || {}))}</p>
            </div>
          </div>

          {/* Whose account this is. Impersonation makes this essential: without
              a name an admin cannot tell which person's Bills & Invoices they opened. */}
          {!ut.accountHolder?.name ? (
            // Hold the row so the tabs do not jump down when the name arrives.
            <div className="mt-2 flex">
              <Skeleton className="h-6 w-40 bg-white/20" rounded="rounded-full" />
            </div>
          ) : (
            <div className="mt-2 flex">
              <span
                className={`inline-flex min-w-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
                  impersonating ? "bg-amber-400 text-amber-950" : "bg-white/15 text-white"
                }`}
              >
                <Icon name={impersonating ? "eye-fill" : "person-fill"} size="0.7rem" className="shrink-0" />
                <span className="truncate">
                  {impersonating ? "Viewing as " : ""}
                  {ut.accountHolder.name}
                </span>
                {ut.accountHolder.phone && (
                  <span className={`truncate font-normal ${impersonating ? "text-amber-900" : "text-emerald-50"}`}>
                    · {ut.accountHolder.phone}
                  </span>
                )}
              </span>
            </div>
          )}

          {/* Tabs */}
          <div className="mt-3 inline-flex w-full gap-1 rounded-xl bg-white/15 p-1 sm:w-auto">
            {TABS.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => setTab(item.key)}
                className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg px-4 py-2 text-sm font-bold transition-colors sm:flex-none ${
                  activeTab === item.key ? "bg-white text-teal-700 shadow-sm" : "text-white/90 hover:bg-white/10"
                }`}
              >
                <Icon name={item.icon} size="0.85rem" />
                {item.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-5">
        {/* One history of the person's own Bills & Invoices money, on both tabs.
            It replaces the Statements button on the Dashboard card. */}
        <AllTransactionsButton onClick={() => setAllTransactionsOpen(true)} className="mb-4 sm:max-w-sm" />

        {ut.error && (
          <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
            {ut.error}
          </div>
        )}

        {/* Rendered while the module loads too — the panel has its own
            placeholder, so the sections below never shift up or down. */}
        <MonthlySummaryPanel
          endpoint="/ut-bills/monthly-summary"
          title="Monthly Summary"
          subtitle={activeTab === "billing" ? "Companies you bill" : "Bills you pay"}
          icon="receipt"
          accent="teal"
          sectionKey={activeTab === "billing" ? "Billing" : "My Bills"}
          buildTransactionsUrl={buildUtBillsTransactionsUrl}
          showBalanceColumn={activeTab === "billing"}
          renderExtraTile={activeTab === "billing" ? renderUtilityExpenseTile : undefined}
          renderStatAction={activeTab === "billing" ? renderExpenseAction : undefined}
          refreshKey={summaryRefreshKey || undefined}
          emptyText={
            activeTab === "billing"
              ? "No billing company activity for this period."
              : "No bills for this period."
          }
        />

        {ut.loading ? (
          <LoadingSkeleton />
        ) : activeTab === "bills" ? (
          <BillsView
            wallet={ut.wallet}
            topUpEnabled={ut.topUpEnabled}
            invoices={ut.invoices}
            outstandingTotal={ut.outstandingTotal}
            utilityStatement={ut.utilityStatement}
            statementRows={ut.statementRows}
            statementSummary={ut.statementSummary}
            onAddFunds={ut.addFunds}
            prepaymentCredit={ut.prepaymentCredit}
            onPrepaid={ut.reloadCustomerSide}
            autopayCompanies={ut.autopayCompanies}
            focusAutopay={location.state?.focus === "autopay"}
            onSetAutopayApproval={ut.setAutopayApproval}
            onPayBill={ut.payBillFromWallet}
            onObjectBill={ut.objectToBill}
          />
        ) : (
          <BillingConsole
            providers={ut.providers}
            selectedProviderId={ut.selectedProviderId}
            setSelectedProviderId={ut.setSelectedProviderId}
            selectedProvider={ut.selectedProvider}
            accounts={ut.accounts}
            categories={ut.categories}
            providerInvoices={ut.providerInvoices}
            providerStatement={ut.providerStatement}
            providerCustomers={ut.providerCustomers}
            providerCustomersLoading={ut.providerCustomersLoading}
            onLoadCustomerStatement={ut.fetchCustomerStatement}
            onCreateCategory={ut.createCategory}
            onConfigureAutoInvoice={ut.configureCategoryAutoInvoice}
            onCreateInvoice={ut.createInvoice}
            onCreateBill={ut.createBill}
            onWaiveInvoice={ut.waiveInvoice}
            onEditInvoice={ut.editInvoice}
            onDeleteInvoice={ut.deleteInvoice}
            onSearchCustomers={ut.searchCustomers}
            onRequestAutopayApproval={ut.requestAutopayApproval}
            registeredUserName={ut.accountHolder?.name || ""}
            onProviderRegistered={handleProviderRegistered}
            deepLinkProviderId={location.state?.providerId}
            deepLinkModal={location.state?.openModal}
            deepLinkPropertyId={location.state?.propertyId}
            deepLinkUnitId={location.state?.unitId}
          />
        )}

        {expenseModalOpen && (
          <ExpenseRecordModal
            providers={ut.providers}
            onCreate={createExpenseRecord}
            onClose={() => setExpenseModalOpen(false)}
          />
        )}

        {allTransactionsOpen && (
          <AllTransactionsModal config={BILLS_ALL_TRANSACTIONS} onClose={() => setAllTransactionsOpen(false)} />
        )}
      </main>
    </div>
  )
}

function AddExpenseButton({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Add expense record"
      className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-emerald-100 bg-white px-2 py-1 text-[11px] font-bold text-emerald-700 transition-colors hover:border-emerald-200 hover:bg-emerald-50 focus:outline-none focus:ring-2 focus:ring-emerald-300"
    >
      <Icon name="plus-lg" size="0.75rem" />
      Add expense
    </button>
  )
}

function UtilityNetCashTile({ summary = {} }) {
  const netCash = Number(summary.netCash || 0)
  const isLoss = netCash < 0
  const valueTone = isLoss
    ? "text-rose-600"
    : netCash > 0
      ? "text-emerald-700"
      : "text-slate-900"

  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50 p-3 transition-colors hover:bg-slate-100/70">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] font-medium text-slate-500">Net cash</p>
          <p className={`mt-1 text-base font-black leading-tight ${valueTone}`}>
            {formatMoney(netCash)}
          </p>
        </div>
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white text-emerald-700 ring-1 ring-slate-100">
          <Icon name={isLoss ? "graph-down-arrow" : "graph-up-arrow"} size="1rem" />
        </div>
      </div>
    </div>
  )
}

// Same shape as the loaded tab: the three wallet/debt tiles, then the
// statement table underneath.
function LoadingSkeleton() {
  return (
    <div className="space-y-4" aria-busy="true" aria-label="Loading Bills &amp; Invoices">
      <SkeletonStatGrid count={3} />
      <SkeletonTable rows={5} columns={5} />
    </div>
  )
}

export default UTBillsModule
